import { HttpClient } from '@angular/common/http';
import { computed, inject, Injectable, signal } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { CaseDefinition } from 'src/api';
import { environment } from 'src/environments/environment';
import { DemoCaseBundle } from './demo-case-bundle';
import { RawDemoCaseIndexEntry } from './demo-export.types';
import { toCaseDefinition } from './demo-api.mappers';

/**
 * Demo mode: read-only cases served from static JSON on the downloads bucket instead of the API.
 *
 * Nothing here runs for a normal case. The overview page loads {@link loadDemoCases} only when
 * the user picks the demo filter, and {@link demoApiInterceptor} short-circuits on
 * {@link active} being false, so the cost on the normal path is one signal read per request.
 */
@Injectable({ providedIn: 'root' })
export class DemoCasesService {
  private readonly http = inject(HttpClient);

  private readonly _index = signal<RawDemoCaseIndexEntry[]>([]);
  private readonly _bundles = signal<Record<string, DemoCaseBundle>>({});
  private readonly loading = new Map<string, Promise<DemoCaseBundle | undefined>>();
  private indexRequest: Promise<void> | undefined;

  /** Demo cases as the overview page consumes them. */
  readonly demoCases = computed<CaseDefinition[]>(() =>
    this._index().map((entry) => toCaseDefinition(entry)),
  );

  /**
   * True once any demo case bundle is loaded. The interceptor uses this as its guard, so demo
   * handling stays completely inert until a user actually opens a demo case.
   */
  readonly active = computed(() => Object.keys(this._bundles()).length > 0);

  get enabled(): boolean {
    return !!environment.demoCasesIndexUrl;
  }

  async loadDemoCases(): Promise<void> {
    if (!this.enabled) return;
    // Concurrent callers (overview filter change plus a deep link into a demo case) share the
    // same request rather than each fetching the index.
    this.indexRequest ??= this.fetchIndex();
    return this.indexRequest;
  }

  isDemoCase(caseId: string | null | undefined): boolean {
    if (!caseId) return false;
    return this._index().some((entry) => entry.id === caseId);
  }

  bundle(caseId: string): DemoCaseBundle | undefined {
    return this._bundles()[caseId];
  }

  /**
   * Resolves the bundle a request belongs to. Most endpoints are addressed by case id, but
   * file/chat/state endpoints only carry their own id, so those are looked up across the
   * bundles that are already loaded.
   */
  bundleForCase(caseId: string | null | undefined): DemoCaseBundle | undefined {
    return caseId ? this._bundles()[caseId] : undefined;
  }

  bundleForFile(fileId: string): DemoCaseBundle | undefined {
    return this.findBundle((bundle) => bundle.filesById.has(fileId));
  }

  bundleForChat(chatId: string): DemoCaseBundle | undefined {
    return this.findBundle((bundle) => bundle.chatsById.has(chatId));
  }

  bundleForState(stateId: string): DemoCaseBundle | undefined {
    return this.findBundle((bundle) => bundle.stateIndexById.has(stateId));
  }

  bundleForFileAction(fileActionId: string): DemoCaseBundle | undefined {
    return this.findBundle((bundle) =>
      bundle.files.some((file) => file.actions.some((action) => action.id === fileActionId)),
    );
  }

  /** Loads (and caches) a demo case's data. Called from the case resolver before any page renders. */
  async loadCase(caseId: string): Promise<DemoCaseBundle | undefined> {
    if (!this.enabled) return undefined;

    const loaded = this._bundles()[caseId];
    if (loaded) return loaded;

    const inFlight = this.loading.get(caseId);
    if (inFlight) return inFlight;

    await this.loadDemoCases();
    const entry = this._index().find((candidate) => candidate.id === caseId);
    if (!entry) return undefined;

    const baseUrl = this.caseBaseUrl(entry);
    const pending = DemoCaseBundle.load(this.http, caseId, baseUrl)
      .then((bundle) => {
        this._bundles.update((bundles) => ({ ...bundles, [caseId]: bundle }));
        return bundle;
      })
      .catch((error) => {
        // Returning undefined lets the caller fall through to the real API (which will report
        // the case as missing) instead of the route dying with an unexplained rejection.
        console.error(`Failed to load demo case ${caseId} from ${baseUrl}:`, error);
        return undefined;
      })
      .finally(() => this.loading.delete(caseId));

    this.loading.set(caseId, pending);
    return pending;
  }

  private async fetchIndex(): Promise<void> {
    const indexUrl = environment.demoCasesIndexUrl;
    if (!indexUrl) return;

    try {
      const entries = await firstValueFrom(this.http.get<RawDemoCaseIndexEntry[]>(indexUrl));
      this._index.set(entries ?? []);
    } catch (error) {
      // Demo cases are an optional extra: a missing or broken index must not break the
      // overview page, so log and carry on with an empty list.
      console.error('Failed to load demo cases index:', error);
      this._index.set([]);
      this.indexRequest = undefined;
    }
  }

  /**
   * Where a case's data lives. Accepts an absolute URL or a path relative to the index, under
   * either spelling of the key, and falls back to the case id — which is the layout the export
   * produces, so an index that only lists ids still works.
   */
  private caseBaseUrl(entry: RawDemoCaseIndexEntry): string {
    const location = entry.downloadUrl ?? entry.download_url ?? entry.id;

    if (/^https?:\/\//i.test(location)) {
      return location.replace(/\/+$/, '');
    }

    const indexUrl = environment.demoCasesIndexUrl ?? '';
    const root = indexUrl.slice(0, indexUrl.lastIndexOf('/'));
    return `${root}/${location.replace(/^\/+|\/+$/g, '')}`;
  }

  private findBundle(predicate: (bundle: DemoCaseBundle) => boolean): DemoCaseBundle | undefined {
    return Object.values(this._bundles()).find(predicate);
  }
}
